import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/Button';
import CustomSelect from '../../components/CustomSelect';
import { useUserProfile } from '../../hooks/useUserProfile';
import AddTaskModal from '../../components/vendor/AddTaskModal';
import TaskDetailModal from '../../components/TaskDetailModal';
import enhancedApiClient from '../../api/enhancedApiClient';

const VendorChecklist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState('All Items');
  const [eventFilter, setEventFilter] = useState('All Events');
  const [searchQuery, setSearchQuery] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventNames, setEventNames] = useState({});
  const [creatorNames, setCreatorNames] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [hasInitialized, setHasInitialized] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);

  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);
  const vendorId = userProfile?.currentVendorId;

  const fetchTasks = async () => {
    setLoading(true);
    if (!vendorId) { setLoading(false); return; }

    try {
      const eventsRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/events`);
      const eventsData = eventsRes.ok ? await eventsRes.json() : [];
      setEvents(eventsData);

      const names = {};
      for (const e of eventsData) { names[e.id] = e.name; }
      setEventNames(names);

      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/tasks`);
      const tasksData = response.ok ? await response.json() : [];
      setTasks(tasksData);

      const creators = {};
      for (const task of tasksData) {
        const uid = task.createdByUserId;
        if (uid && !creators[uid]) {
          if (uid === 'system') { creators[uid] = 'Planning Guide'; continue; }
          if (uid === currentUser?.username) { creators[uid] = 'You'; continue; }
          try {
            const profile = await enhancedApiClient.getUser(uid);
            creators[uid] = profile?.name || 'Client';
          } catch { creators[uid] = 'Client'; }
        }
      }
      setCreatorNames(creators);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, [vendorId, currentUser?.username]);

  // Auto-open task from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('taskId');
    if (taskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === taskId);
      if (task) { setSelectedTask(task); setShowDetailView(true); }
    }
  }, [location.search, tasks]);

  // Auto-collapse on first load
  useEffect(() => {
    if (!hasInitialized && tasks.length > 0) {
      setHasInitialized(true);
      const phases = ['6plus', '3to6', '1to3', 'final'];
      let currentPhase = null;
      for (const phase of phases) {
        if (tasks.some(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === phase)) {
          currentPhase = phase; break;
        }
      }
      if (currentPhase) {
        const collapsed = { completed: true };
        for (const phase of phases) {
          if (phase !== currentPhase) collapsed[phase] = true;
        }
        setCollapsedSections(collapsed);
      }
    }
  }, [tasks, hasInitialized]);

  const getEventName = (task) => {
    const eventId = task.PK?.replace('EVENT#', '') || task.eventId;
    return eventNames[eventId] || 'Unknown Event';
  };

  const getEventDate = (task) => {
    const eventId = task.PK?.replace('EVENT#', '') || task.eventId;
    const event = events.find(e => e.id === eventId);
    return event?.eventDate;
  };

  const getCreatorName = (uid) => creatorNames[uid] || 'Unknown';

  const getResponsibilityStatus = (task) => {
    const isCreatedByVendor = task.createdByUserId === currentUser?.username;
    const isSystem = task.createdByUserId === 'system';
    const isCompleted = task.status === 'completed';

    if (isSystem) return isCompleted ? { text: 'Completed', color: '#059669', bgColor: '#D1FAE5' } : { text: 'Client Action Required', color: '#6B7280', bgColor: '#F3F4F6' };
    if (isCreatedByVendor) return isCompleted ? { text: 'Completed by Client', color: '#059669', bgColor: '#D1FAE5' } : { text: 'Awaiting Client Action', color: '#DC2626', bgColor: '#FEE2E2' };
    return isCompleted ? { text: 'Completed - Awaiting Your Confirmation', color: '#F59E0B', bgColor: '#FEF3C7' } : { text: 'Pending Your Action', color: '#6B7280', bgColor: '#F3F4F6' };
  };

  const getDueDateInfo = (dueDate, status) => {
    if (!dueDate || status === 'completed') return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`, color: '#DC2626', isOverdue: true };
    if (diffDays === 0) return { text: 'Due today', color: '#F59E0B', isOverdue: false };
    if (diffDays === 1) return { text: 'Due tomorrow', color: '#6B7280', isOverdue: false };
    return { text: `Due in ${diffDays} days`, color: '#6B7280', isOverdue: false };
  };

  const getTimeGroup = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const today = new Date();
    const diffMonths = (due - today) / (1000 * 60 * 60 * 24 * 30);
    if (diffMonths < 1) return 'final';
    if (diffMonths < 3) return '1to3';
    if (diffMonths < 6) return '3to6';
    return '6plus';
  };

  const uniqueEventNames = [...new Set(tasks.map(t => getEventName(t)))];

  const filteredTasks = tasks.filter(task => {
    if (searchQuery && !task.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (activeTab === 'completed' && task.status !== 'completed') return false;
    if (activeTab === 'overdue') {
      if (task.status === 'completed' || !task.dueDate) return false;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (new Date(task.dueDate) >= today) return false;
    }
    if (activeTab === 'upcoming' && task.status === 'completed') return false;
    if (timeFilter !== 'all' && task.status !== 'completed') {
      if (getTimeGroup(task.dueDate) !== timeFilter) return false;
    }
    if (statusFilter === 'Completed' && task.status !== 'completed') return false;
    if (statusFilter === 'Pending' && task.status === 'completed') return false;
    if (eventFilter !== 'All Events' && getEventName(task) !== eventFilter) return false;
    return true;
  }).sort((a, b) => {
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return new Date(a.dueDate) - new Date(b.dueDate);
  });

  const allTaskCounts = {
    '6plus': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === '6plus').length,
    '3to6': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === '3to6').length,
    '1to3': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === '1to3').length,
    'final': tasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === 'final').length,
  };

  const groupedTasks = {
    '6plus': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === '6plus'),
    '3to6': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === '3to6'),
    '1to3': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === '1to3'),
    'final': filteredTasks.filter(t => t.status !== 'completed' && getTimeGroup(t.dueDate) === 'final'),
    'completed': filteredTasks.filter(t => t.status === 'completed')
  };

  const timeGroupLabels = { '6plus': '6+ Months Out', '3to6': '3-6 Months Out', '1to3': '1-3 Months Out', 'final': 'Final Month' };

  const renderTask = (task, index) => {
    const dueDateInfo = getDueDateInfo(task.dueDate, task.status);
    const responsibility = getResponsibilityStatus(task);
    return (
      <div
        key={task.id || index}
        onClick={() => { setSelectedTask(task); setShowDetailView(true); }}
        style={{
          backgroundColor: dueDateInfo?.isOverdue ? '#FEF2F2' : 'white',
          padding: '20px', borderRadius: '12px',
          border: dueDateInfo?.isOverdue ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', gap: '16px',
          cursor: 'pointer', transition: 'all 0.2s', marginBottom: '12px'
        }}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = dueDateInfo?.isOverdue ? '#FEE2E2' : '#F9FAFB'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = dueDateInfo?.isOverdue ? '#FEF2F2' : 'white'}
      >
        <div style={{ width: '48px', height: '48px', backgroundColor: dueDateInfo?.isOverdue ? '#FEE2E2' : '#E8F4E8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={dueDateInfo?.isOverdue ? '#DC2626' : '#6B9080'} strokeWidth="1.5">
            <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span style={{ fontWeight: '600', fontSize: '18px', color: '#1F2937' }}>{task.name}</span>
            {responsibility && (
              <span style={{ fontSize: '12px', fontWeight: '500', color: responsibility.color, backgroundColor: responsibility.bgColor, padding: '3px 10px', borderRadius: '12px' }}>
                {responsibility.text}
              </span>
            )}
          </div>
          <div style={{ color: '#6B7280', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>{task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}</span>
            {dueDateInfo && (<><span>·</span><span style={{ color: dueDateInfo.color, fontWeight: '500' }}>{dueDateInfo.text}</span></>)}
            <span>·</span><span>{getEventName(task)}</span>
            <span>·</span><span>{getCreatorName(task.createdByUserId)}</span>
          </div>
        </div>
        <div style={{
          width: '32px', height: '32px', backgroundColor: task.status === 'completed' ? '#6B9080' : '#E5E7EB',
          borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: task.status === 'completed' ? 'white' : '#6B7280', fontSize: '16px', cursor: 'pointer'
        }} onClick={async (e) => {
          e.stopPropagation();
          try {
            const eventId = task.PK?.replace('EVENT#', '');
            const newStatus = task.status === 'completed' ? 'pending' : 'completed';
            await enhancedApiClient.updateTask(eventId, task.id, { status: newStatus });
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
          } catch (error) { console.error('Failed to update task:', error); }
        }}>
          {task.status === 'completed' ? '✓' : ''}
        </div>
      </div>
    );
  };

  if (loading || profileLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTop: '3px solid #6B9080', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>Loading checklist...</div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header with stats */}
      <div style={{ backgroundColor: '#E8F4E8', padding: '32px 24px 140px 24px', textAlign: 'center', position: 'relative', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '42px', fontWeight: '400', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>Checklist</h1>
        {tasks.length > 0 && (
          <div style={{ fontSize: '16px', color: '#6B7280', marginTop: '8px' }}>
            {Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100)}% Complete
          </div>
        )}

        <div style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', width: '100%', maxWidth: '1200px', padding: '0 24px' }}>
          <div onClick={() => { setActiveTab(activeTab === 'completed' ? 'all' : 'completed'); setStatusFilter('All Items'); setTimeFilter('all'); }}
            style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', border: activeTab === 'completed' ? '2px solid #6B9080' : '2px solid transparent' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937' }}>{tasks.filter(t => t.status === 'completed').length} <span style={{ fontSize: '18px', fontWeight: 'normal' }}>completed</span></div>
          </div>
          <div onClick={() => { setActiveTab(activeTab === 'overdue' ? 'all' : 'overdue'); setStatusFilter('All Items'); setTimeFilter('all'); }}
            style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', border: activeTab === 'overdue' ? '2px solid #6B9080' : '2px solid transparent' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937' }}>{tasks.filter(t => { if (t.status === 'completed' || !t.dueDate) return false; const d = new Date(t.dueDate); d.setHours(0,0,0,0); return d < new Date(new Date().setHours(0,0,0,0)); }).length} <span style={{ fontSize: '18px', fontWeight: 'normal' }}>overdue</span></div>
          </div>
          <div onClick={() => { setActiveTab(activeTab === 'upcoming' ? 'all' : 'upcoming'); setStatusFilter('All Items'); setTimeFilter('all'); }}
            style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', border: activeTab === 'upcoming' ? '2px solid #6B9080' : '2px solid transparent' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937' }}>{tasks.filter(t => t.status !== 'completed').length} <span style={{ fontSize: '18px', fontWeight: 'normal' }}>upcoming</span></div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {/* Search, Filters, Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search checklist..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', backgroundColor: '#F9FAFB' }} />
          <CustomSelect options={['All Items', 'Completed', 'Pending']} defaultValue="All Items" onChange={(value) => { setStatusFilter(value); setActiveTab('all'); }} />
          {uniqueEventNames.length > 1 && (
            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
              style={{ padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white', minWidth: '150px' }}>
              <option>All Events</option>
              {uniqueEventNames.map(name => <option key={name}>{name}</option>)}
            </select>
          )}
          <button onClick={() => setShowAddModal(true)}
            style={{ padding: '10px 20px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add Task
          </button>
          <button
            onClick={() => {
              // Generate runsheet CSV organized by sections
              const sections = {
                'Setup Details': tasks.filter(t => /floor plan|layout|styling|AV|microphone|access/i.test(t.name)),
                'Guest Numbers': tasks.filter(t => /guest|RSVP|seating|dietary/i.test(t.name)),
                'Food & Beverage': tasks.filter(t => /menu|catering|beverage|food|bar/i.test(t.name)),
                'Vendor Contacts': tasks.filter(t => /vendor|external|arrival/i.test(t.name)),
                'Schedule & Timeline': tasks.filter(t => /timeline|run sheet|ceremony|reception|timing|schedule/i.test(t.name)),
                'Payments & Contracts': tasks.filter(t => /contract|deposit|payment|invoice|balance/i.test(t.name)),
                'Other': tasks.filter(t => {
                  const patterns = /floor plan|layout|styling|AV|microphone|access|guest|RSVP|seating|dietary|menu|catering|beverage|food|bar|vendor|external|arrival|timeline|run sheet|ceremony|reception|timing|schedule|contract|deposit|payment|invoice|balance/i;
                  return !patterns.test(t.name);
                })
              };
              let csv = 'Section,Task,Status,Due Date,Description,Sub-tasks\n';
              for (const [section, sectionTasks] of Object.entries(sections)) {
                if (sectionTasks.length === 0) continue;
                for (const t of sectionTasks) {
                  const subTasksStr = (t.subTasks || []).map(st => `${st.completed ? '[x]' : '[ ]'} ${st.text}`).join('; ') || '';
                  const desc = (t.details || t.description || '').replace(/"/g, '""');
                  csv += `"${section}","${t.name}","${t.status || 'pending'}","${t.dueDate || ''}","${desc}","${subTasksStr}"\n`;
                }
              }
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `runsheet-${eventFilter !== 'All Events' ? eventFilter.replace(/\s+/g, '-').toLowerCase() : 'all-events'}-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
            }}
            style={{ padding: '10px 20px', backgroundColor: 'white', color: '#6B9080', border: '1.5px solid #6B9080', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Export Runsheet
          </button>
        </div>

        {/* Time Period Filter Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', overflowX: 'auto', paddingBottom: '4px' }}>
          {[{ key: 'all', label: 'All Times' }, { key: '6plus', label: `6+ Months (${allTaskCounts['6plus']})` }, { key: '3to6', label: `3-6 Months (${allTaskCounts['3to6']})` }, { key: '1to3', label: `1-3 Months (${allTaskCounts['1to3']})` }, { key: 'final', label: `Final Month (${allTaskCounts['final']})` }].map(tab => (
            <button key={tab.key} onClick={() => { setTimeFilter(tab.key); setActiveTab('all'); }}
              style={{ padding: '8px 16px', backgroundColor: timeFilter === tab.key ? '#6B9080' : 'white', color: timeFilter === tab.key ? 'white' : '#6B7280', border: timeFilter === tab.key ? 'none' : '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Grouped Task List */}
        {timeFilter === 'all' && activeTab === 'all' ? (
          <>
            {['6plus', '3to6', '1to3', 'final'].map(group => {
              const groupTasks = groupedTasks[group];
              if (groupTasks.length === 0) return null;
              const isCollapsed = collapsedSections[group];
              return (
                <div key={group} style={{ marginBottom: '24px' }}>
                  <div onClick={() => setCollapsedSections(prev => ({ ...prev, [group]: !prev[group] }))}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: isCollapsed ? '0' : '12px', paddingBottom: '8px', borderBottom: '2px solid #E5E7EB' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{timeGroupLabels[group]} ({groupTasks.length})</h3>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6,9 12,15 18,9"/></svg>
                  </div>
                  {!isCollapsed && groupTasks.map(renderTask)}
                </div>
              );
            })}
            {groupedTasks.completed.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div onClick={() => setCollapsedSections(prev => ({ ...prev, completed: !prev.completed }))}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: collapsedSections.completed ? '0' : '12px', paddingBottom: '8px', borderBottom: '2px solid #E5E7EB' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', margin: 0 }}>Completed ({groupedTasks.completed.length})</h3>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" style={{ transform: collapsedSections.completed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6,9 12,15 18,9"/></svg>
                </div>
                {!collapsedSections.completed && groupedTasks.completed.map(renderTask)}
              </div>
            )}
          </>
        ) : filteredTasks.length === 0 ? (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#6B7280' }}>No tasks found</div>
        ) : (
          filteredTasks.map(renderTask)
        )}
      </div>

      <AddTaskModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onTaskAdded={() => fetchTasks()} />
      <TaskDetailModal
        task={showDetailView ? selectedTask : null}
        onClose={() => { setShowDetailView(false); setSelectedTask(null); }}
        eventId={selectedTask?.PK?.replace('EVENT#', '') || selectedTask?.eventId}
        currentUser={currentUser}
        onStatusUpdate={fetchTasks}
      />
    </div>
  );
};

export default VendorChecklist;
